import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { FixedSizeList as List } from 'react-window';

import PickerModal, {
  ListItem,
  PickerModalNoData,
  PickerModalSearch,
} from '../../../shared/_TBComponents/PickerModal';
import Avatar from '../../../shared/_TBComponents/Avatar/Avatar';
import Spinner from '../../../shared/_TBComponents/Spinner';

import { journalTracker } from '../../../shared/utils/amplitudeHelper';
import { setCurrentGroup, setJournalTab, setSelectedAthlete } from '../ducks/journalActions';

const AthletesContainer = styled('div')`
  height: 400px;
  margin: 8px auto 0 auto;
  width: 100%;
`;

const getAthleteFullName = (athlete) => `${athlete.first} ${athlete.last}`;

const AthleteItem = (props) => <ListItem {...props} />;

const Row = ({ data, index, style }) => {
  const { athletes, selectAthlete } = data;
  const athlete = athletes[index];
  const name = getAthleteFullName(athlete);

  return (
    <AthleteItem
      listItemWidth='calc(100% - 24px)'
      style={{ ...style, left: '20px', width: 'calc(100% - 24px)' }}
      itemMarginLeft='16px'
      onClick={() => selectAthlete(athlete)}
      aria-label={`Select athlete: ${name}`}
    >
      <Avatar avatar={athlete.pic || ''} name={name} size={28} />
      <p>{name}</p>
    </AthleteItem>
  );
};

Row.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  index: PropTypes.number.isRequired,
  style: PropTypes.instanceOf(Object).isRequired,
};

const AthletePickerModal = ({ onRequestClose, scrollToTop }) => {
  const { currentUser } = useSelector((state) => state.auth.data);
  const { allAthletes: athletes, usersError, currentGroup } = useSelector(
    (state) => state.journal.data,
  );
  const { usersLoading } = useSelector((state) => state.journal.ui);
  const [searchString, setSearchString] = useState('');

  const dispatch = useDispatch();

  const myJournal = {
    first: 'My',
    id: currentUser?.id,
    last: 'Journal',
    pic: currentUser?.pic,
  };
  const athletesArrayWithMyJournal = Array.isArray(athletes)
    ? [myJournal, ...athletes]
    : [myJournal];

  const filteredAthletes = useMemo(
    () => athletesArrayWithMyJournal.filter((athlete) => getAthleteFullName(athlete)
      .toLowerCase()
      .includes(searchString.toLowerCase())),
    [athletesArrayWithMyJournal, searchString],
  );
  const athletesAvailable = filteredAthletes.length > 0 && !usersError;

  const handleSelectAthlete = (athlete) => {
    const selectedMyJournal = athlete.id === currentUser?.id;
    if (currentGroup?.id) {
      dispatch(setCurrentGroup(null, null));
    }
    dispatch(setJournalTab('SINGLE_ATHLETE'));
    dispatch(setSelectedAthlete(athlete));
    onRequestClose();
    scrollToTop();
    journalTracker('Athlete picker modal', `${selectedMyJournal ? 'My Journal' : 'Athlete'} selected`);
  };

  const renderNoData = () => {
    let message;
    if (usersError) {
      message = usersError?.message || usersError?.response?.data?.message;
    } else if (searchString.length) {
      message = 'No athletes found matching your search.';
    } else {
      message = 'No athletes found.';
    }

    return <PickerModalNoData message={message} />;
  };

  const renderAthletesList = () => {
    if (usersLoading) {
      return <Spinner large aria-label='Loading athletes...' />;
    }

    if (!athletesAvailable) {
      return renderNoData();
    }

    return (
      <List
        style={{ overflowX: 'hidden' }}
        className='athlete-list'
        height={filteredAthletes.length > 0 ? 400 : 50}
        width='100%'
        itemData={{
          athletes: filteredAthletes,
          selectAthlete: handleSelectAthlete,
        }}
        itemSize={50}
        itemCount={filteredAthletes?.length}
      >
        {Row}
      </List>
    );
  };

  return (
    <PickerModal
      headerText='Select an Athlete&apos;s Journal'
      headerIcon='user'
      iconSize={24}
      onRequestClose={onRequestClose}
    >
      <PickerModalSearch
        placeholder='Search athlete by name'
        ariaLabel='Search athletes by name'
        value={searchString}
        onChange={(e) => setSearchString(e.target.value)}
      />
      <AthletesContainer hasData={athletesAvailable}>
        {renderAthletesList()}
      </AthletesContainer>
    </PickerModal>
  );
};

AthletePickerModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
  scrollToTop: PropTypes.func.isRequired,
};

export default AthletePickerModal;
